// src/hooks/useAppSettings.js
import { useEffect, useState } from "react";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { storage, db } from "../firebaseClient";
import { useAuth } from "../contexts/AuthContext";
import { logDebug } from "../lib/logger";
import { handleError } from "../lib/errorUtils";
import { retryFirestore } from "../lib/retry";

const CEDAR_SPRINGS_LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/central-control-system-478401.firebasestorage.app/o/logos%2F1c815b6c-2ea1-40fd-9e4a-a7aa32065c2f--0dd8c013-b35b-441c-8120-c230a783dc6c.webp?alt=media&token=e3b737ae-e4e3-40ce-8568-65a06cc04e55";

const resolveStorageUrl = async (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  if (/^gs:\/\//i.test(url)) {
    try {
      return await getDownloadURL(ref(storage, url));
    } catch {
      return "";
    }
  }

  return url;
};

/**
 * useAppSettings
 *
 * Fetches and manages app settings from Firestore.
 * Settings are stored in a single document: app_settings/config
 */
export function useAppSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    // Don't fetch settings if user is not authenticated (e.g., on login page)
    if (!currentUser) {
      setLoading(false);
      return;
    }

    async function fetchSettings() {
      logDebug("[useAppSettings] Fetching settings");
      setLoading(true);
      setError(null);

      try {
        const docRef = doc(db, "app_settings", "config");
        const docSnap = await retryFirestore(() => getDoc(docRef), {
          maxRetries: 3,
          initialDelay: 500,
        });
        logDebug("[useAppSettings] Document exists", docSnap.exists());

        if (docSnap.exists()) {
          const currentSettings = docSnap.data();
          const stores = Array.isArray(currentSettings.stores) ? currentSettings.stores : [];
          const hasCedarSprings = stores.some((store) => store.name === "Cedar Springs");
          const storeRecipients = currentSettings.storeRecipients || {};
          const needsRecipient = !storeRecipients["Cedar Springs"];
          const needsBranding = stores.some(
            (store) =>
              store.name === "Cedar Springs" &&
              (!store.brandName || !store.logoUrl || store.logoUrl === "")
          );

          const updatedStores = hasCedarSprings
            ? stores.map((store) =>
                store.name === "Cedar Springs"
                  ? {
                      ...store,
                      brandName: store.brandName || "Space Cadet Cannabis",
                      logoUrl: store.logoUrl || CEDAR_SPRINGS_LOGO_URL,
                    }
                  : store
              )
            : [
                ...stores,
                {
                  name: "Cedar Springs",
                  brandName: "Space Cadet Cannabis",
                  logoUrl: CEDAR_SPRINGS_LOGO_URL,
                  code: "CED",
                  pos: "flowhub",
                  url: "",
                },
              ];
          const updatedRecipients = {
            ...storeRecipients,
            "Cedar Springs":
              storeRecipients["Cedar Springs"] ||
              "josh@medscafe.com, jake@medscafe.com, al@medscafe.com",
          };

          const resolvedStores = await Promise.all(
            updatedStores.map(async (store) => {
              const resolvedLogoUrl = await resolveStorageUrl(store.logoUrl);
              return resolvedLogoUrl && resolvedLogoUrl !== store.logoUrl
                ? { ...store, logoUrl: resolvedLogoUrl }
                : store;
            })
          );
          const resolvedLogoUrl = await resolveStorageUrl(currentSettings.medscafeLogoUrl);
          const resolvedSpaceCadetLogoUrl = await resolveStorageUrl(
            currentSettings.spaceCadetLogoUrl
          );

          const mergedSettings = {
            ...currentSettings,
            stores: resolvedStores,
            storeRecipients: updatedRecipients,
            medscafeLogoUrl: resolvedLogoUrl || currentSettings.medscafeLogoUrl || "",
            spaceCadetLogoUrl: resolvedSpaceCadetLogoUrl || currentSettings.spaceCadetLogoUrl || "",
          };

          const storesChanged = resolvedStores.some(
            (store, index) => store.logoUrl !== updatedStores[index]?.logoUrl
          );
          const logoChanged =
            resolvedLogoUrl &&
            resolvedLogoUrl !== currentSettings.medscafeLogoUrl &&
            /^https?:\/\//i.test(resolvedLogoUrl);
          const spaceCadetLogoChanged =
            resolvedSpaceCadetLogoUrl &&
            resolvedSpaceCadetLogoUrl !== currentSettings.spaceCadetLogoUrl &&
            /^https?:\/\//i.test(resolvedSpaceCadetLogoUrl);

          if (
            hasCedarSprings &&
            !needsRecipient &&
            !needsBranding &&
            !storesChanged &&
            !logoChanged &&
            !spaceCadetLogoChanged
          ) {
            setSettings(currentSettings);
          } else {
            setSettings(mergedSettings);
            await retryFirestore(
              () =>
                updateDoc(docRef, {
                  stores: resolvedStores,
                  storeRecipients: updatedRecipients,
                  medscafeLogoUrl: mergedSettings.medscafeLogoUrl,
                  spaceCadetLogoUrl: mergedSettings.spaceCadetLogoUrl,
                  updatedAt: serverTimestamp(),
                }),
              { maxRetries: 2 }
            );
          }
        } else {
          // Initialize with defaults if document doesn't exist
          const defaultSettings = {
            stores: [
              {
                name: "Cheboygan",
                code: "CHB",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Alpena",
                code: "ALP",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Gaylord",
                code: "GAY",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Lowell",
                code: "LOW",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Manistee",
                code: "MAN",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Rogers City",
                code: "RC",
                pos: "flowhub",
                url: "",
              },
              {
                name: "Cedar Springs",
                brandName: "Space Cadet Cannabis",
                logoUrl: CEDAR_SPRINGS_LOGO_URL,
                code: "CED",
                pos: "flowhub",
                url: "",
              },
            ],
            storeRecipients: {
              "Rogers City":
                "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, joseph@medscafe.com", // pragma: allowlist secret
              Gaylord: "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, jared@medscafe.com",
              Lowell: "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, alex@medscafe.com",
              Alpena: "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, secoya@medscafe.com",
              Manistee:
                "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, destinee@medscafe.com",
              Cheboygan:
                "josh@medscafe.com, jake@medscafe.com, al@medscafe.com, brian@medscafe.com",
              "Cedar Springs": "josh@medscafe.com, jake@medscafe.com, al@medscafe.com",
              "Traverse City": "josh@medscafe.com, jake@medscafe.com, al@medscafe.com",
            },
            defaultRecipient: "josh@medscafe.com",
            ownerEmails: "josh@medscafe.com, jake@medscafe.com",
            supplyManagerEmail: "alex@medscafe.com",
            merchInventoryAnalysisRecipients: "alex@medscafe.com",
            expirationDaysAhead: 45,
            medscafeLogoUrl: "",
            spaceCadetLogoUrl: "",
            googleChatWebhookUrl: "",
            weeklyReportRetentionWeeks: 12,
            weeklyReportAnonymizeCustomerNames: false,
            pdfServiceUrl: "https://weekly-reports-pdf-generator-lszg2cmyfa-uc.a.run.app",
            posterServiceUrl: "https://poster-generator-lszg2cmyfa-uc.a.run.app",
            customSupplyItems: [],
            restockPriorityScope: "selected",
            restockPriorityBoost: 5,
            updatedAt: serverTimestamp(),
          };
          await retryFirestore(() => setDoc(docRef, defaultSettings), {
            maxRetries: 2,
          });
          // Get the data back to ensure we have the proper format
          const savedDoc = await retryFirestore(() => getDoc(docRef), {
            maxRetries: 2,
          });
          if (savedDoc.exists()) {
            setSettings(savedDoc.data());
          } else {
            setSettings(defaultSettings);
          }
        }
      } catch (err) {
        handleError(err, {
          source: "useAppSettings",
          action: "fetchSettings",
        });
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchSettings();
  }, [currentUser]);

  const updateSettings = async (updates) => {
    try {
      const docRef = doc(db, "app_settings", "config");
      await retryFirestore(
        () =>
          updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
          }),
        {
          maxRetries: 2,
        }
      );
      // Refresh settings after update
      const updatedDoc = await retryFirestore(() => getDoc(docRef), {
        maxRetries: 2,
      });
      if (updatedDoc.exists()) {
        setSettings(updatedDoc.data());
      } else {
        setSettings((prev) => ({ ...prev, ...updates }));
      }
      return true;
    } catch (err) {
      handleError(err, {
        source: "useAppSettings",
        action: "updateSettings",
        updates,
      });
      throw err;
    }
  };

  return { settings, loading, error, updateSettings };
}
